<script setup lang="ts">
import { ref, onErrorCaptured } from 'vue';
import { RouterLink } from 'vue-router';

const error = ref<Error | null>(null);
const errorInfo = ref<string>('');

onErrorCaptured((err: unknown, _, info) => {
    error.value = err instanceof Error ? err : new Error(String(err));
    errorInfo.value = info;
    console.error('Error caught by boundary:', err, info);
    return false; // Prevent the error from propagating
});

const resetError = () => {
    error.value = null;
    errorInfo.value = '';
};

defineExpose({ resetError });

const refreshPage = () => window.location.reload();
</script>

<template>
    <div v-if="error" class="flex flex-col items-center justify-center min-h-screen p-4">
        <div class="max-w-md w-full bg-card border border-destructive rounded-lg p-6 space-y-4">
            <div class="space-y-2">
                <h1 class="text-2xl font-bold text-destructive">Something went wrong</h1>
                <p class="text-muted-foreground">
                    An unexpected error occurred. Please try refreshing the page or contact support if the problem
                    persists.
                </p>
            </div>

            <details v-if="error" class="mt-4">
                <summary class="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                    Error details
                </summary>
                <div class="mt-2 p-3 bg-muted rounded text-sm font-mono text-destructive overflow-auto">
                    <div class="font-semibold">{{ error.name }}</div>
                    <div>{{ error.message }}</div>
                    <div v-if="errorInfo" class="mt-2 text-xs text-muted-foreground">{{ errorInfo }}</div>
                </div>
            </details>

            <div class="flex gap-2 mt-6">
                <button
                    @click="resetError"
                    class="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                    Try again
                </button>
                <RouterLink
                    to="/"
                    class="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors text-center"
                >
                    Go home
                </RouterLink>
                <button
                    @click="refreshPage"
                    class="px-4 py-2 bg-outline text-foreground rounded-md hover:bg-outline/90 transition-colors"
                >
                    Refresh page
                </button>
            </div>
        </div>
    </div>
    <slot v-else />
</template>
